import type { QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";

import { planKeys, projectKeys, recordKeys } from "@/lib/query-keys";
import type {
  PaginatedResponse,
  ProjectDetail,
  ProjectPlan,
  ProjectRecord,
} from "@/types/api";

import {
  deletePinnedProject,
  getAllPinnedProjects,
  getAssetKeysByProject,
  getPinnedProject,
  putPinnedProject,
  type PinnedProject,
  type PinSource,
} from "./db";
import {
  createSemaphore,
  ensureAssetCached,
  invalidateCachedAsset,
  invalidateCachedAssetsByProject,
} from "./blobs";
import {
  type AssetDownload,
  planDownloadsFrom,
  planIdFromAssetKey,
  recordDownloadsFrom,
} from "./asset-keys";
import { isOnline } from "./online-status";
import { markPinnedInCache, markUnpinnedInCache, pinKeys } from "./pins";
import { normalizeRecordsPage } from "./record-shape";

/** Same shape as `useAuthenticatedFetch` returns, but typed for non-React callers. */
export type AuthFetch = <T>(path: string, init?: RequestInit) => Promise<T>;

const RECORDS_PAGE_SIZE = 100;
export const RECORDS_PAGES = 3; // 3 × 100 = 300 newest records (server orders by image.taken_at DESC).
const PLANS_PAGE_SIZE = 100;
const PLANS_PAGES_MAX = 50; // safety cap; stops early when getNextPageParam → undefined.
const DOWNLOAD_CONCURRENCY = 4;

/**
 * Invalidate only the per-project pin status. Used per-task during a sync so
 * the active PinToggle re-reads progress without thrashing every other
 * `usePinnedProjects` subscriber on the screen (the project list can have
 * dozens of cards mounted at once).
 */
function notifyPinStatus(queryClient: QueryClient, projectId: string): void {
  void queryClient.invalidateQueries({
    queryKey: pinKeys.status(projectId),
  });
}

/**
 * Invalidate everything pin-related. Used on terminal state transitions
 * (initial syncing, ready, error) and on unpin/resync so the global list view
 * (Settings, hero card) refreshes.
 */
function notifyAllPinSubscribers(
  queryClient: QueryClient,
  projectId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: pinKeys.list() });
  void queryClient.invalidateQueries({ queryKey: pinKeys.status(projectId) });
}

async function fetchAllRecords(
  queryClient: QueryClient,
  fetchWithAuth: AuthFetch,
  projectId: string,
): Promise<ProjectRecord[]> {
  const result = await queryClient.fetchInfiniteQuery({
    queryKey: recordKeys.list(projectId, {}),
    queryFn: async ({ pageParam }) =>
      // v2.0 multi-photo read (FE-1): the offline pin must cache *every* photo
      // of a record (`images[]`), not just the legacy cover, so it reads the
      // same v2.0 shape as the on-screen grid. Normalize the page to the
      // canonical `{ image, thumbnail }` shape here so the cached records and
      // the asset downloader (`recordDownloadsFrom`) resolve real URLs — the
      // v2.0 wire carries only `*_url` derivatives. See record-shape.
      normalizeRecordsPage(
        await fetchWithAuth<PaginatedResponse<ProjectRecord>>(
          `/projects/2.0/projects/${projectId}/records/?page=${pageParam}&page_size=${RECORDS_PAGE_SIZE}`,
        ),
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    pages: RECORDS_PAGES,
  });
  return result.pages.flatMap((page) => page.results);
}

async function fetchAllPlans(
  queryClient: QueryClient,
  fetchWithAuth: AuthFetch,
  projectId: string,
): Promise<ProjectPlan[]> {
  const result = await queryClient.fetchInfiniteQuery({
    queryKey: planKeys.list(projectId),
    queryFn: ({ pageParam }) =>
      fetchWithAuth<PaginatedResponse<ProjectPlan>>(
        `/projects/1.0/projects/${projectId}/plans/?page=${pageParam}&page_size=${PLANS_PAGE_SIZE}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    pages: PLANS_PAGES_MAX,
  });
  return result.pages.flatMap((page) => page.results);
}

function isQuotaError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "QuotaExceededError";
}

/**
 * Write the pinned-project row only if the row is still pinned (the user
 * hasn't unpinned underneath us). Returns true if the write happened. Closes
 * the race where an in-flight pinProject can resurrect a row that
 * unpinProject already deleted.
 */
async function safePutIfStillPinned(
  next: PinnedProject,
): Promise<boolean> {
  const current = await getPinnedProject(next.projectId);
  if (!current) return false;
  await putPinnedProject(next);
  return true;
}

// ---------------------------------------------------------------------------
// In-flight registries — coalesce repeated calls for the same project so two
// rapid taps (or a tap colliding with an on-mount resync) don't race against
// the same IDB row.
// ---------------------------------------------------------------------------

const pinInflight = new Map<string, Promise<void>>();
const resyncInflight = new Map<string, Promise<void>>();

// ---------------------------------------------------------------------------
// pinProject
// ---------------------------------------------------------------------------

interface PinProjectOpts {
  projectId: string;
  queryClient: QueryClient;
  fetchWithAuth: AuthFetch;
  /**
   * Suppress all user-facing toasts (offline / quota / failure). Auto-cache-on-open
   * passes this so a pin the user didn't ask for stays silent; failures and quota
   * exhaustion still reach Sentry. Defaults to false (manual pins stay loud).
   */
  silent?: boolean;
  /** Why this pin is happening — see {@link PinSource}. Defaults to `"manual"`. */
  source?: PinSource;
}

/**
 * Resolve the source to persist on a pin row. **Manual always wins:** a manual
 * pin upgrades an existing `"auto"` row to `"manual"`, and an `"auto"` trigger
 * never downgrades an existing pin (a row that predates this field is read as the
 * legacy `"manual"`). A brand-new pin with no existing row keeps the incoming
 * source. See the auto-cache-on-open spec.
 */
export function resolvePinSource(
  existing: Pick<PinnedProject, "source"> | undefined,
  incoming: PinSource,
): PinSource {
  if (incoming === "manual") return "manual";
  if (!existing) return "auto";
  return existing.source ?? "manual";
}

/**
 * Download a project's metadata, 300 newest records (thumbnails only) and all
 * plans (full-resolution) into IDB. Concurrent calls for the same project
 * coalesce to a single in-flight orchestrator. Idempotent: a retry after a
 * partial failure resumes by skipping already-stored assets.
 *
 * Failure modes:
 *
 * - **Offline at call time** — toast, no state change.
 * - **`QuotaExceededError` during downloads** — status flips to `"error"`,
 *   partial assets retained, sonner toast surfaces the storage-full message.
 * - **Network error** — status flips to `"error"`, Sentry-reported, re-thrown
 *   so the caller can react.
 */
export function pinProject(opts: PinProjectOpts): Promise<void> {
  const existing = pinInflight.get(opts.projectId);
  if (existing) return existing;
  const promise = runPin(opts).finally(() => {
    pinInflight.delete(opts.projectId);
  });
  pinInflight.set(opts.projectId, promise);
  return promise;
}

async function runPin({
  projectId,
  queryClient,
  fetchWithAuth,
  silent = false,
  source: incomingSource = "manual",
}: PinProjectOpts): Promise<void> {
  if (!isOnline()) {
    if (!silent) toast("You're offline — connect to pin this project.");
    return;
  }

  const existing = await getPinnedProject(projectId);
  const isFirstPin = !existing;
  const source = resolvePinSource(existing, incomingSource);

  const initial: PinnedProject = {
    projectId,
    pinned: true,
    lastSyncedAt: existing?.lastSyncedAt ?? null,
    status: "syncing",
    progress: {
      recordsDownloaded: 0,
      recordsTotal: 0,
      plansDownloaded: 0,
      plansTotal: 0,
    },
    sizeBytes: existing?.sizeBytes ?? 0,
    source,
  };
  await putPinnedProject(initial);
  markPinnedInCache(projectId);
  notifyAllPinSubscribers(queryClient, projectId);

  // Counters that survive across the per-task closures so we can write a
  // single final row that reflects everything that did and didn't land.
  let recordsDone = 0;
  let plansDone = 0;
  let totalBytes = 0;
  let quotaHit = false;
  // Keys written to IDB during this orchestrator's lifetime. If `unpinProject`
  // runs concurrently and races a queued download, the post-flight cleanup
  // walks this set and removes any blob that landed after the pin row was
  // deleted. Without this, an unpin can appear successful while orphaned
  // blobs remain in IDB for the same projectId.
  const newlyWrittenKeys = new Set<string>();

  try {
    await queryClient.prefetchQuery({
      queryKey: projectKeys.detail(projectId),
      queryFn: () =>
        fetchWithAuth<ProjectDetail>(`/projects/1.0/projects/${projectId}/`),
    });

    const [records, plans] = await Promise.all([
      fetchAllRecords(queryClient, fetchWithAuth, projectId),
      fetchAllPlans(queryClient, fetchWithAuth, projectId),
    ]);

    const recordDownloads = recordDownloadsFrom(records);
    const planDownloads = planDownloadsFrom(plans);
    const recordsTotal = recordDownloads.length;
    const plansTotal = planDownloads.length;

    const writeProgress = async (
      status: "syncing" | "ready" | "error",
    ): Promise<boolean> => {
      const next: PinnedProject = {
        projectId,
        pinned: true,
        lastSyncedAt: status === "ready" ? Date.now() : initial.lastSyncedAt,
        status,
        progress: {
          recordsDownloaded: recordsDone,
          recordsTotal,
          plansDownloaded: plansDone,
          plansTotal,
        },
        sizeBytes: totalBytes,
        source,
      };
      const wrote = await safePutIfStillPinned(next);
      if (!wrote) return false;
      if (status === "syncing") notifyPinStatus(queryClient, projectId);
      else notifyAllPinSubscribers(queryClient, projectId);
      return true;
    };

    // Initial write that surfaces the now-known totals.
    if (!(await writeProgress("syncing"))) return;

    const run = createSemaphore(DOWNLOAD_CONCURRENCY);

    async function downloadOne(
      task: AssetDownload,
      kind: "record" | "plan",
    ): Promise<void> {
      // Re-check at every await boundary so the rest of the bounded-concurrency
      // pool short-circuits the moment we know we've exceeded quota or the
      // user unpinned underneath us.
      if (quotaHit) return;
      if (!(await getPinnedProject(projectId))) return;
      try {
        // `ensureAssetCached` skips a durably-cached blob, so a retry after a
        // partial pin resumes cheaply. Every processed key is tracked for the
        // unpin-race cleanup below.
        const blob = await ensureAssetCached(task.key, task.url, projectId);
        newlyWrittenKeys.add(task.key);
        if (quotaHit) return;
        totalBytes += blob.size;
        if (kind === "record") recordsDone += 1;
        else plansDone += 1;
        await writeProgress("syncing");
      } catch (err) {
        if (isQuotaError(err)) {
          quotaHit = true;
          return;
        }
        throw err;
      }
    }

    // Promise.allSettled so a single failing download never short-circuits the
    // pool — the worst case is we mark the project "error" once at the end
    // with whatever partial progress made it through.
    const results = await Promise.allSettled([
      ...recordDownloads.map((d) => run(() => downloadOne(d, "record"))),
      ...planDownloads.map((d) => run(() => downloadOne(d, "plan"))),
    ]);
    const rejected = results.find((r) => r.status === "rejected");

    // Post-flight cancellation cleanup: if `unpinProject` deleted the row
    // while our queue was draining, every blob we wrote belongs to a project
    // that no longer wants offline data. Walk the writes and remove them.
    if (!(await getPinnedProject(projectId))) {
      await Promise.all(
        [...newlyWrittenKeys].map((key) =>
          invalidateCachedAsset(key).catch(() => undefined),
        ),
      );
      return;
    }

    if (quotaHit) {
      await writeProgress("error");
      if (silent) {
        // Auto-cache shows no toast, so capture the quota miss to keep telemetry.
        Sentry.captureException(new Error("Auto-cache hit storage quota"), {
          tags: { area: "offline-sync", phase: "pin-quota", projectId },
        });
      } else {
        toast(
          "Storage full — couldn't finish pinning this project. Free up space and tap to retry.",
        );
      }
      return;
    }
    if (rejected) {
      // Bubble out so the outer catch handles Sentry + toast + state.
      throw (rejected as PromiseRejectedResult).reason;
    }

    await writeProgress("ready");

    if (isFirstPin && navigator.storage?.persist) {
      try {
        await navigator.storage.persist();
      } catch (err) {
        Sentry.captureException(err, {
          tags: { area: "offline-sync", phase: "storage-persist" },
        });
      }
    }
  } catch (err) {
    // Use the running counters (not `initial`'s zeros) so the error row keeps
    // whatever progress made it through before the failure.
    await safePutIfStillPinned({
      projectId,
      pinned: true,
      lastSyncedAt: initial.lastSyncedAt,
      status: "error",
      progress: {
        recordsDownloaded: recordsDone,
        recordsTotal: recordsDone, // best estimate when we don't know totals
        plansDownloaded: plansDone,
        plansTotal: plansDone,
      },
      sizeBytes: totalBytes,
      source,
    });
    notifyAllPinSubscribers(queryClient, projectId);
    Sentry.captureException(err, {
      tags: { area: "offline-sync", phase: "pin", projectId },
    });
    if (!silent) toast("Pinning failed — tap to retry.");
    throw err;
  }
}

// ---------------------------------------------------------------------------
// unpinProject
// ---------------------------------------------------------------------------

interface UnpinProjectOpts {
  projectId: string;
  queryClient: QueryClient;
}

/**
 * Remove a project's offline footprint. Drops the pinnedProjects row, the
 * persisted query slots (detail, records-by-project, plans-by-project) and
 * every cached blob keyed to this project. Other projects' data is untouched.
 */
export async function unpinProject({
  projectId,
  queryClient,
}: UnpinProjectOpts): Promise<void> {
  await deletePinnedProject(projectId);
  markUnpinnedInCache(projectId);

  queryClient.removeQueries({ queryKey: projectKeys.detail(projectId) });
  queryClient.removeQueries({ queryKey: recordKeys.byProject(projectId) });
  queryClient.removeQueries({ queryKey: planKeys.byProject(projectId) });

  await invalidateCachedAssetsByProject(projectId);
  notifyAllPinSubscribers(queryClient, projectId);
}

// ---------------------------------------------------------------------------
// resyncPinnedProjects
// ---------------------------------------------------------------------------

interface ResyncOpts {
  queryClient: QueryClient;
  fetchWithAuth: AuthFetch;
}

/**
 * Re-fetch every pinned project's metadata, then top up any newly-arrived
 * record thumbnails and replace any plan blobs whose `latest_revision` rolled
 * forward. Skips already-stored assets so this is cheap to re-run. Failures
 * for one project don't take down others.
 */
export async function resyncPinnedProjects({
  queryClient,
  fetchWithAuth,
}: ResyncOpts): Promise<void> {
  if (!isOnline()) return;
  const projects = await getAllPinnedProjects();
  if (projects.length === 0) return;

  await Promise.all(
    projects.map((project) => {
      const projectId = project.projectId;
      const existing = resyncInflight.get(projectId);
      if (existing) return existing;
      const promise = resyncOneProject(project, queryClient, fetchWithAuth)
        .catch((err) => {
          Sentry.captureException(err, {
            tags: { area: "offline-sync", phase: "resync", projectId },
          });
        })
        .finally(() => resyncInflight.delete(projectId));
      resyncInflight.set(projectId, promise);
      return promise;
    }),
  );
}

async function resyncOneProject(
  project: PinnedProject,
  queryClient: QueryClient,
  fetchWithAuth: AuthFetch,
): Promise<void> {
  const projectId = project.projectId;

  await queryClient.prefetchQuery({
    queryKey: projectKeys.detail(projectId),
    queryFn: () =>
      fetchWithAuth<ProjectDetail>(`/projects/1.0/projects/${projectId}/`),
  });

  const [records, plans] = await Promise.all([
    fetchAllRecords(queryClient, fetchWithAuth, projectId),
    fetchAllPlans(queryClient, fetchWithAuth, projectId),
  ]);

  const existingKeys = new Set(await getAssetKeysByProject(projectId));

  const newRecordDownloads = recordDownloadsFrom(records).filter(
    (d) => !existingKeys.has(d.key),
  );

  // Track desired plan state (newKey per planId). Used after downloads to
  // figure out which stale-keyed blobs are safe to evict.
  const desiredPlanKeyByPlanId = new Map<string, string>();
  const newPlanDownloads: AssetDownload[] = [];
  for (const download of planDownloadsFrom(plans)) {
    const planId = planIdFromAssetKey(download.key);
    if (planId) desiredPlanKeyByPlanId.set(planId, download.key);
    if (!existingKeys.has(download.key)) {
      newPlanDownloads.push(download);
    }
  }

  // Pre-compute stale plan keys without acting on them yet. The eviction has
  // to wait until we know which replacements actually landed — otherwise a
  // failed plan-revision download would leave the project with NO offline
  // copy of that plan (the old blob deleted, the new one missing).
  //
  // A key is stale when it's a plan key whose planId either no longer appears
  // in the server response (plan deleted) or whose desired revision key
  // differs from this one (revision rolled forward).
  const stalePlanKeys = [...existingKeys].filter((key) => {
    const planId = planIdFromAssetKey(key);
    if (!planId) return false;
    return desiredPlanKeyByPlanId.get(planId) !== key;
  });

  const run = createSemaphore(DOWNLOAD_CONCURRENCY);
  // Track download outcomes so we know whether it's safe to evict and whether
  // to bump lastSyncedAt.
  const landedKeys = new Set<string>();
  let downloadFailures = 0;

  const tryDownload = async (d: AssetDownload): Promise<void> => {
    try {
      await ensureAssetCached(d.key, d.url, projectId);
      landedKeys.add(d.key);
    } catch (err) {
      downloadFailures += 1;
      Sentry.captureException(err, {
        tags: { area: "offline-sync", phase: "resync-download", projectId },
      });
    }
  };

  await Promise.allSettled([
    ...newRecordDownloads.map((d) => run(() => tryDownload(d))),
    ...newPlanDownloads.map((d) => run(() => tryDownload(d))),
  ]);

  // Now decide which stale plan blobs we can safely evict. Two cases are
  // safe: (a) the plan was deleted server-side (no desired key), or (b) the
  // replacement blob is on disk (either pre-existing under the new revision
  // key, or freshly landed in this resync).
  const safeToEvict: string[] = [];
  for (const stale of stalePlanKeys) {
    const planId = planIdFromAssetKey(stale);
    if (!planId) continue;
    const desired = desiredPlanKeyByPlanId.get(planId);
    if (!desired) {
      // Plan removed from the project. Safe to evict the offline copy.
      safeToEvict.push(stale);
      continue;
    }
    const replacementOnDisk =
      existingKeys.has(desired) || landedKeys.has(desired);
    if (replacementOnDisk) {
      safeToEvict.push(stale);
    }
    // else: keep the stale blob so the user still has SOMETHING offline
    // until the next resync succeeds.
  }
  await Promise.all(
    safeToEvict.map((key) => invalidateCachedAsset(key)),
  );

  // Re-check the row hasn't been unpinned mid-resync. Only bump
  // `lastSyncedAt` on a fully successful resync — partial failures leave the
  // timestamp stale as an implicit signal that something didn't refresh.
  await safePutIfStillPinned({
    ...project,
    status: project.status === "error" ? "error" : "ready",
    lastSyncedAt: downloadFailures === 0 ? Date.now() : project.lastSyncedAt,
  });
  notifyAllPinSubscribers(queryClient, projectId);
}

/** Test-only: reset the in-flight registries so each test starts clean. */
export function resetSyncRegistriesForTesting(): void {
  pinInflight.clear();
  resyncInflight.clear();
}
